import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';
import { useToken } from '../../../hooks/useToken';

// Constantes para los tipos de tablero
export const BOARD_TYPES = {
  GENERAL: 'general',
  SOPORTE: 'soporte',
  FLUJOS_BOTS: 'flujo_bot', // Tipo "flujo_bot" deshabilitado en la UI hasta implementar la lógica
  BOT: 'bot' // Tipo "bot" oculto en la UI hasta implementar la lógica
};

// Constantes para las claves del API (valores exactos que espera el servidor)
export const BOARD_TYPES_API = {
  GENERAL: 'general',
  SOPORTE: 'soporte',
  FLUJOS_BOTS: 'flujo_bot',
  BOT: 'bot',
  SUPPORT: 'soporte'  // Añadir mapeo para el valor 'support' (inglés)
};


// Mapeo para mostrar títulos legibles
export const BOARD_TYPE_TITLES = {
  [BOARD_TYPES.GENERAL]: 'General',
  [BOARD_TYPES.SOPORTE]: 'Soporte',
  [BOARD_TYPES.FLUJOS_BOTS]: 'Flujo Bot',
  [BOARD_TYPES.BOT]: 'Bot'
};

/**
 * Modal para crear un nuevo tablero
 * Sigue el principio de responsabilidad única (SRP) de SOLID
 */
const CreateBoardModal = ({ open, onClose, onSave, boardType }) => {
  const { getTokenSilent } = useToken();
  const agent = useSelector(state => state.agent);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados para el formulario básico
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPinned, setIsPinned] = useState(false);

  // Estado para manejar errores de validación
  const [, setErrors] = useState({});

  // Estado para manejar metadatos adicionales
  const [metaDataFields, setMetaDataFields] = useState([]);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [showAddMetaField, setShowAddMetaField] = useState(false);

  // Efectos para el manejo del formulario
  useEffect(() => {
    resetForm();
  }, [boardType]);

  // Limpiar el formulario cuando el modal se cierra o se abre
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  /**
   * Capitaliza la primera letra de un texto y convierte el resto a minúsculas
   * @param {string} text - Texto a capitalizar
   * @returns {string} Texto con la primera letra en mayúscula y el resto en minúsculas
   */
  const capitalizeFirstLetter = (text) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  // Depurar información del agente
  useEffect(() => {
    // Logs eliminados
  }, [agent]);

  // Manejar cambios en los campos
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      setName(value);
    } else if (name === 'description') {
      setDescription(value);
    } else if (name === 'board_type') {
      // Handle board_type change
    }
  };

  // Función para validar el formulario
  const validateForm = () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = 'El nombre del tablero es obligatorio';
    }

    const hasRequiredData = (agent?._id || agent?.id) && agent?.id_customer;
    if (!hasRequiredData) {
      console.error('Faltan datos del agente o cliente');
       return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Función para añadir un nuevo campo de metadatos
  const handleAddMetaField = () => {
    if (newKey.trim() && newValue.trim()) {
      setMetaDataFields([
        ...metaDataFields,
        { key: newKey.trim(), value: newValue.trim() }
      ]);
      setNewKey('');
      setNewValue('');
      setShowAddMetaField(false);
    }
  };

  // Función para eliminar un campo de metadatos
  const handleRemoveMetaField = (index) => {
    const updatedFields = [...metaDataFields];
    updatedFields.splice(index, 1);
    setMetaDataFields(updatedFields);
  };

  // Función para crear el objeto de metadatos en formato {"key":"value"}
  const buildMetaData = () => {
    // Crear un objeto base solo con la información personalizada
    const metaData = {};

    // Añadir descripción solo si existe y solo para compatibilidad
    if (description && description.trim()) {
      metaData.descripcion = description.trim();
    }

    // Añadir solo los campos personalizados que el usuario ingresó
    metaDataFields.forEach(field => {
      metaData[field.key] = field.value;
    });

    // Retornar como string JSON
    return JSON.stringify(metaData);
  };

  // Manejar envío del formulario
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validar el formulario primero
      if (!validateForm()) {
        setLoading(false);
        return;
      }

      // Obtener el token
      const token = await getTokenSilent();

      if (!token) {
        throw new Error('No se pudo obtener el token de autenticación');
      }

      // Validar datos requeridos
      if (!agent) {
        throw new Error('No se encontró información del agente');
      }

      if (!agent.id_customer) {
        throw new Error('No se encontró información del cliente');
      }

      // Obtener el ID del agente como string
      let agentId = String(agent._id || agent.id);

      // Asegurarnos de que created_by sea un string válido
      let createdById = agentId;

      // Si no hay un ID válido, usar un valor predeterminado
      if (!createdById || createdById.trim() === '') {
        createdById = "1";
        console.warn('ID de agente no disponible, usando "1" como valor predeterminado');
      }

      // Preparar datos del tablero
      const boardData = {
        name: capitalizeFirstLetter(name.trim()), // Primera letra mayúscula, resto minúsculas
        created_by: createdById, // Usar el valor como string
        id_customer: String(agent.id_customer),
        id_agent: agentId,
        board_type: boardType || BOARD_TYPES.GENERAL,
        pinned: Boolean(isPinned),
        meta_data: buildMetaData()
      };

      // Añadir token para la autenticación
      boardData.token = token;



      // Llamar a la función onSave con los datos
      await onSave(boardData);

      // Limpiar el formulario y cerrar el modal
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error al guardar el tablero:', error);
      setError(error.message || 'Error al crear el tablero');
    } finally {
      setLoading(false);
    }
  };

  // Función para resetear el formulario
  const resetForm = () => {
    setName('');
    setDescription('');
    setIsPinned(false);
    setMetaDataFields([]);
    setNewKey('');
    setNewValue('');
    setShowAddMetaField(false);
    setErrors({});
  };

  // Función para cerrar el modal
  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Crear Nuevo Tablero {boardType && `- ${BOARD_TYPE_TITLES[boardType]}`}
      </DialogTitle>

      <form onSubmit={handleSave}>
        <DialogContent>
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Nombre del tablero"
            type="text"
            fullWidth
            required
            value={name}
            onChange={handleChange}
            disabled={loading}
          />

          <TextField
            margin="dense"
            name="description"
            label="Descripción (opcional)"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={description}
            onChange={handleChange}
            disabled={loading}
          />

          {!boardType && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Tipo de tablero</InputLabel>
              <Select
                name="board_type"
                value={boardType || BOARD_TYPES.GENERAL}
                onChange={handleChange}
                required
                disabled={loading}
              >
                {Object.entries(BOARD_TYPES)
                  // Filtrar el tipo "bot" completamente
                  .filter(([key, value]) => value !== BOARD_TYPES.BOT)
                  .map(([key, value]) => (
                    <MenuItem
                      key={value}
                      value={value}
                    >
                      {BOARD_TYPE_TITLES[value]}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          {/* Metadatos adicionales */}
          <Box sx={{ mt: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1">Información adicional</Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setShowAddMetaField(true)}
                variant="outlined"
              >
                Agregar campo
              </Button>
            </Box>

            {/* Información sobre qué son los metadatos */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Los metadatos son información adicional que se guardará junto con el tablero.
              Se incluirán automáticamente: la fecha de creación y el nombre del agente.
            </Typography>

            {/* Mostrar campos de metadatos existentes */}
            {metaDataFields.length > 0 && (
              <Box sx={{ mb: 2 }}>
                {metaDataFields.map((field, index) => (
                  <Chip
                    key={index}
                    label={`${field.key}: ${field.value}`}
                    onDelete={() => handleRemoveMetaField(index)}
                    deleteIcon={<DeleteIcon />}
                    sx={{ m: 0.5 }}
                  />
                ))}
              </Box>
            )}

            {/* Formulario para añadir nuevo campo de metadatos */}
            {showAddMetaField && (
              <Grid container spacing={2} sx={{ mb: 2, mt: 1 }}>
                <Grid item xs={5}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Clave"
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    placeholder="Ej: SKU"
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Valor"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    placeholder="Ej: ABC123"
                  />
                </Grid>
                <Grid item xs={2} display="flex" alignItems="center">
                  <Tooltip title="Añadir">
                    {!newKey.trim() || !newValue.trim() ? (
                      <span>
                        <IconButton
                          color="primary"
                          disabled={true}
                        >
                          <AddIcon />
                        </IconButton>
                      </span>
                    ) : (
                      <IconButton
                        color="primary"
                        onClick={handleAddMetaField}
                      >
                        <AddIcon />
                      </IconButton>
                    )}
                  </Tooltip>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Opción para fijar tablero */}
          <FormControlLabel
            control={
              <Switch
                checked={isPinned}
                onChange={(e) => setIsPinned(e.target.checked)}
                color="primary"
              />
            }
            label="Fijar tablero"
            sx={{ mt: 2 }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !name.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Creando...' : 'Crear'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateBoardModal;